/**
 * Change origin: telling the draft audit log what caused a save, and which
 * fields in it the form filled in rather than the person typing them.
 *
 * Without this every save reads as the person's own edit — including a netto
 * the Transaksi tab summed from the goods on mount, an incoterm the payload
 * defaulted to EXW, or a kemasan the "follow Kemasan tab" helper rewrote. Two
 * optional request headers carry the account (backend
 * domain/ceisa/draft_audit/constants):
 *
 *  - X-Change-Source: what caused the request as a whole. Every draft write
 *    sends one — "manual:tab-save" for a plain save, a helper's own code when a
 *    helper saves by itself.
 *  - X-Field-Origins: inside a plain save, the fields the form set itself, as
 *    JSON {rowKey: {field: code}}. Row keys and field names are the audit
 *    snapshot's (the backend model's JSON names), not the form's, which is what
 *    each tab's field map below translates.
 *
 * A code is "<kind>:<name>". The CMS colours by kind and labels by code
 * (cms-jarvis src/components/draft-audit/format.ts ORIGIN_LABELS); a new code
 * works without a CMS change, it just shows as "<Kind> · <name>".
 */

import type { AxiosRequestConfig } from 'axios';

export type OriginKind = 'manual' | 'auto' | 'default' | 'helper' | 'sync';
export type OriginCode = `${OriginKind}:${string}`;

export const CHANGE_SOURCE_HEADER = 'X-Change-Source';
export const FIELD_ORIGINS_HEADER = 'X-Field-Origins';

/**
 * Every code the editors send. Named here so a call site cannot misspell one
 * and silently start a new category in the log.
 */
export const ORIGIN = {
  // Plain saves: whatever in them is untagged, the person typed.
  tabSave: 'manual:tab-save',
  barangDrawer: 'manual:barang-drawer',
  barangRow: 'manual:barang-row',
  save: 'manual:save',
  addRow: 'manual:add-row',
  deleteRow: 'manual:delete-row',
  documentSave: 'manual:document-save',
  documentGoods: 'manual:document-goods',
  documentUpload: 'manual:document-upload',
  submit: 'manual:submit',
  restoreRow: 'manual:restore-row',
  gate: 'manual:gate',

  // Automatic: the form did it with nobody clicking anything.
  barangSummary: 'auto:barang-summary',
  kurs: 'auto:kurs',
  cifCalc: 'auto:cif-calc',
  freightInsuranceCalc: 'auto:freight-insurance-calc',
  extraction: 'auto:extraction',
  masterData: 'auto:master-data',
  lookup: 'auto:lookup',
  headerSync: 'auto:header-sync',
  sessionRestore: 'auto:session-restore',
  normalise: 'auto:normalise',
  renumber: 'auto:renumber',
  autoTariffSync: 'auto:tariff-sync',
  autoKomponenBiaya: 'auto:komponen-biaya',
  susulanExtract: 'auto:susulan-extract',
  documentLinks: 'auto:document-links',
  lartasMatch: 'auto:lartas-match',

  // Silent defaults: a value the form or the payload filled in for an empty
  // field. The name says which default, so a reader can see "EXW" came from
  // the fallback rather than an invoice.
  defaultValue: 'default:form-default',
  defaultZero: 'default:zero',
  defaultPlaceholder: 'default:placeholder',
  defaultToday: 'default:today',
  defaultTariffRows: 'default:tariff-rows',

  // Helpers: a person clicked a convenience that filled the value.
  hsSuggestion: 'helper:hs-suggestion',
  hsPick: 'helper:hs-pick',
  uraianFromHs: 'helper:uraian-from-hs',
  kemasanFollow: 'helper:kemasan-follow',
  bulkEdit: 'helper:bulk-edit',
  merkTipeBulk: 'helper:merk-tipe-bulk',
  tariffSync: 'helper:tariff-sync',
  calculateCif: 'helper:calculate-cif',
  revertCif: 'helper:revert-cif',
  calculateFreightInsurance: 'helper:calculate-freight-insurance',
  samakanNetto: 'helper:samakan-netto',
  samakanHarga: 'helper:samakan-harga',
  freightImage: 'helper:freight-image',
  komponenBiaya: 'helper:komponen-biaya',
  salin: 'helper:salin',
  npwpLookup: 'helper:npwp-lookup',
  consigneeApply: 'helper:consignee-apply',
  eksportirApply: 'helper:eksportir-apply',
  historyApply: 'helper:history-apply',
  historyPick: 'helper:history-pick',
  portSuggestion: 'helper:port-suggestion',
  vesselFlag: 'helper:vessel-flag',
  bc11Manifest: 'helper:bc11-manifest',
  derived: 'helper:derived',
  pernyataanPick: 'helper:pernyataan-pick',
  pernyataanSync: 'helper:pernyataan-sync',
  lartasGoods: 'helper:lartas-goods',
  exportirLibrary: 'helper:exportir-library',
  beaKeluar: 'helper:bea-keluar',
  smartSync: 'helper:smart-sync',
  pkbTemplate: 'helper:pkb-template',
  excelUpload: 'helper:excel-upload',
  documentReorder: 'helper:document-reorder',
  barangDatabase: 'helper:barang-database',
  applyToSameHs: 'helper:apply-to-same-hs',

  historicalSync: 'sync:historical',
} as const satisfies Record<string, OriginCode>;

/** rowKey -> audit field name -> code. */
export type FieldOrigins = Record<string, Record<string, OriginCode>>;

/** Tags a field on every row of the save (e.g. one column across all goods). */
export const ANY_ROW = '*';

/**
 * The request config that carries a change's origin. Spread into an axios call:
 *   axiosInstance.patch(url, body, originConfig(ORIGIN.tabSave, origins))
 * Empty field origins send no second header, so a save that the form did not
 * touch looks exactly like a typed one.
 */
export function originConfig(source: OriginCode, fieldOrigins?: FieldOrigins | null): AxiosRequestConfig {
  return { headers: originHeaders(source, fieldOrigins) };
}

export function originHeaders(source: OriginCode, fieldOrigins?: FieldOrigins | null): Record<string, string> {
  const headers: Record<string, string> = { [CHANGE_SOURCE_HEADER]: source };
  try {
    const encoded = encodeFieldOrigins(compactOrigins(fieldOrigins));
    if (encoded) headers[FIELD_ORIGINS_HEADER] = encoded;
  } catch {
    // The tags are a description of the save, never a reason for it to fail.
  }
  return headers;
}

/**
 * Header budget for X-Field-Origins. nginx refuses a header line over 8 KB
 * (large_client_header_buffers) with a 400, which would fail the save itself;
 * the backend reads at most 16 KB. Rows past the budget are dropped whole.
 */
export const FIELD_ORIGINS_MAX_LENGTH = 6000;

/**
 * JSON with every non-ASCII character escaped. Row keys carry document and
 * container numbers as typed or extracted, and a browser refuses to send a
 * header holding a character above U+00FF (setRequestHeader throws), which
 * would fail the save. "\uXXXX" is still JSON, so the backend reads back the
 * same key.
 */
function encodeFieldOrigins(origins: FieldOrigins | null): string | null {
  if (!origins) return null;
  const ascii = (value: unknown) =>
    JSON.stringify(value).replace(/[\u007f-\uffff]/g, (c) => `\\u${c.charCodeAt(0).toString(16).padStart(4, '0')}`);
  const kept: string[] = [];
  let length = 2;
  for (const [row, fields] of Object.entries(origins)) {
    const entry = `${ascii(row)}:${ascii(fields)}`;
    if (length + entry.length + 1 > FIELD_ORIGINS_MAX_LENGTH) continue;
    kept.push(entry);
    length += entry.length + 1;
  }
  return kept.length > 0 ? `{${kept.join(',')}}` : null;
}

function compactOrigins(fieldOrigins?: FieldOrigins | null): FieldOrigins | null {
  if (!fieldOrigins) return null;
  const out: FieldOrigins = {};
  for (const [row, fields] of Object.entries(fieldOrigins)) {
    if (fields && Object.keys(fields).length > 0) out[row] = fields;
  }
  return Object.keys(out).length > 0 ? out : null;
}

/** Merges field origins; a later argument wins for the same row and field. */
export function mergeOrigins(...parts: Array<FieldOrigins | null | undefined>): FieldOrigins {
  const out: FieldOrigins = {};
  for (const part of parts) {
    if (!part) continue;
    for (const [row, fields] of Object.entries(part)) {
      out[row] = { ...(out[row] ?? {}), ...fields };
    }
  }
  return out;
}

// ------------------------------------------------------------------ tracking --

/**
 * Two values are "the same" when a save would send the same thing: numbers and
 * numeric strings compare as numbers, empties compare equal, and a select's
 * {code, name} object compares by code. Loose on purpose — the question is only
 * "did the person replace what the form put there?".
 */
export function sameValue(a: unknown, b: unknown): boolean {
  const norm = (v: unknown): unknown => {
    if (v === undefined || v === null || v === '') return null;
    if (typeof v === 'object' && !Array.isArray(v) && v !== null && 'code' in v) {
      return norm((v as { code: unknown }).code);
    }
    if (typeof v === 'string') {
      const trimmed = v.trim();
      if (trimmed === '') return null;
      const n = Number(trimmed);
      return Number.isFinite(n) && /^-?\d+(\.\d+)?$/.test(trimmed) ? n : trimmed;
    }
    if (typeof v === 'number' && !Number.isFinite(v)) return null;
    return v;
  };
  const x = norm(a);
  const y = norm(b);
  if (typeof x === 'number' && typeof y === 'number') return Math.abs(x - y) < 1e-9;
  if (typeof x === 'object' || typeof y === 'object') return JSON.stringify(x) === JSON.stringify(y);
  return x === y;
}

type Mark = { code: OriginCode; value: unknown };

/**
 * One spelling per field path: Formik accepts both "entitas[1].nama" and
 * "entitas.1.nama", and a mark set one way must match a map written the other.
 */
export const normalizePath = (path: string): string => path.replace(/\[(\d+)\]/g, '.$1').replace(/^\./, '');

/**
 * Remembers which form fields the code set, and to what.
 *
 * It never has to watch the person: at save time a mark whose field still
 * holds the value the form put there is the form's, and a mark whose field now
 * holds something else was typed over and is dropped. So only code paths call
 * mark(); the inputs are left alone.
 *
 * Keys are the form's own field paths ("netto", "entitas.1.namaEntitas",
 * "barang.3.kodeKemasan") — the translation to audit field names happens once,
 * in each tab's save.
 */
export class OriginTracker {
  private marks = new Map<string, Mark>();

  mark(field: string, value: unknown, code: OriginCode): void {
    this.marks.set(normalizePath(field), { code, value });
  }

  /** Several fields at once, all from the same cause. */
  markAll(values: Record<string, unknown>, code: OriginCode, prefix = ''): void {
    for (const [field, value] of Object.entries(values)) {
      this.mark(prefix + field, value, code);
    }
  }

  unmark(field: string): void {
    this.marks.delete(normalizePath(field));
  }

  /**
   * The marks that still hold, as field -> code. `current` reads a field's
   * value at save time (e.g. formik's getIn over the values being saved).
   */
  live(current: (field: string) => unknown, fields?: Iterable<string>): Map<string, OriginCode> {
    const out = new Map<string, OriginCode>();
    const wanted = fields ? new Set([...fields].map(normalizePath)) : null;
    for (const [field, mark] of this.marks) {
      if (wanted && !wanted.has(field)) continue;
      if (sameValue(current(field), mark.value)) out.set(field, mark.code);
    }
    return out;
  }

  /**
   * Forgets marks: all of them when an editor or the goods drawer opens on
   * another draft or line, or some when their rows are reshuffled.
   *
   * A save does not clear what it sent. A mark only ever applies while its
   * field still holds the value the form put there, and once saved that value
   * is the stored one, so a later save cannot record it as a change again.
   * Clearing after a save would also drop marks set while it was in flight,
   * and a field two tabs save (kodePelTujuan) would lose its mark to the first.
   */
  clear(fields?: Iterable<string>): void {
    if (!fields) {
      this.marks.clear();
      return;
    }
    for (const field of fields) this.marks.delete(normalizePath(field));
  }

  /**
   * A list row was removed: its marks go, and the marks of the rows after it
   * move up one index, so each mark stays on the row it was set on.
   */
  removeRow(arrayPath: string, index: number): void {
    const prefix = `${normalizePath(arrayPath)}.`;
    const moved = new Map<string, Mark>();
    for (const [field, mark] of this.marks) {
      if (!field.startsWith(prefix)) continue;
      const [rawIndex, ...rest] = field.slice(prefix.length).split('.');
      const at = Number(rawIndex);
      if (!Number.isInteger(at) || at < index) continue;
      this.marks.delete(field);
      if (at > index) moved.set(`${prefix}${at - 1}${rest.length ? `.${rest.join('.')}` : ''}`, mark);
    }
    for (const [field, mark] of moved) this.marks.set(field, mark);
  }

  /**
   * A list was reordered: `oldIndexOf[newIndex]` is where each row used to be
   * (-1 for a row that is new). Marks follow their rows.
   */
  reorderRows(arrayPath: string, oldIndexOf: readonly number[]): void {
    const prefix = `${normalizePath(arrayPath)}.`;
    const newIndexOf = new Map<number, number>();
    oldIndexOf.forEach((oldIndex, newIndex) => {
      if (oldIndex >= 0) newIndexOf.set(oldIndex, newIndex);
    });
    const moved = new Map<string, Mark>();
    for (const [field, mark] of this.marks) {
      if (!field.startsWith(prefix)) continue;
      const [rawIndex, ...rest] = field.slice(prefix.length).split('.');
      this.marks.delete(field);
      const to = newIndexOf.get(Number(rawIndex));
      if (to !== undefined) moved.set(`${prefix}${to}${rest.length ? `.${rest.join('.')}` : ''}`, mark);
    }
    for (const [field, mark] of moved) this.marks.set(field, mark);
  }

  /** Fields under a prefix, for tabs keyed by row ("barang.3."). */
  fieldsWithPrefix(prefix: string): string[] {
    const normalized = normalizePath(prefix);
    return [...this.marks.keys()].filter((field) => field.startsWith(normalized));
  }
}

/**
 * The editor's tracker. One draft is open at a time; each editor resets it when
 * it mounts, so a mark from the previous draft cannot tag this one's save.
 */
export const originTracker = new OriginTracker();

/**
 * The goods drawer is a form of its own (one line at a time, its own Formik),
 * so its marks live apart from the page's: a drawer field and a page field may
 * share a name, and the drawer resets whenever it opens on another line.
 */
export const barangDrawerOriginTracker = new OriginTracker();

/** Reads a dotted/bracketed path ("entitas[1].nama", "barang.3.hs"). */
export function getPath(values: unknown, path: string): unknown {
  let cursor: unknown = values;
  for (const part of normalizePath(path).split('.')) {
    if (part === '') continue;
    if (cursor === null || cursor === undefined) return undefined;
    cursor = (cursor as Record<string, unknown>)[part];
  }
  return cursor;
}

// biome-ignore lint/suspicious/noExplicitAny: accepts every form's setter, including ones typed to a narrower value
type SetFieldValue = (field: string, value: any, shouldValidate?: boolean) => unknown;

/**
 * setFieldValue for the form's own writes: sets the field and records who set
 * it. Use it in place of setFieldValue in every effect, default and helper that
 * fills a field the person did not type.
 */
export function setFieldFrom(
  setFieldValue: SetFieldValue,
  code: OriginCode,
  field: string,
  value: unknown,
  shouldValidate?: boolean,
  tracker: OriginTracker = originTracker
): void {
  tracker.mark(field, value, code);
  setFieldValue(field, value, shouldValidate);
}

/** setFieldFrom for the goods drawer's own form. */
export function setDrawerFieldFrom(
  setFieldValue: SetFieldValue,
  code: OriginCode,
  field: string,
  value: unknown,
  shouldValidate?: boolean
): void {
  setFieldFrom(setFieldValue, code, field, value, shouldValidate, barangDrawerOriginTracker);
}

/**
 * A form's field -> audit field map. One form field can feed several audit
 * fields (the insurance amount is stored twice, as text and as a number).
 */
export type AuditFieldMap = Record<string, string | readonly string[]>;

// ----------------------------------------------------------- building a save --

/**
 * Collects the field origins of one row of one save.
 *
 * `or` replaces the payload's `value || fallback`: same result, but a fallback
 * that was used is tagged, so a silent default is visible in the log.
 * `fromTracker` adds the form-set fields that survived until the save, through
 * the tab's form-field -> audit-field map.
 */
export class SaveOrigins {
  private origins: FieldOrigins = {};

  constructor(private readonly rowKey: string) {}

  tag(field: string, code: OriginCode, rowKey: string = this.rowKey): this {
    if (!this.origins[rowKey]) this.origins[rowKey] = {};
    this.origins[rowKey][field] = code;
    return this;
  }

  /** `value || fallback`, tagging `field` when the fallback was taken. */
  or<T>(field: string, value: T | null | undefined, fallback: T, code: OriginCode = ORIGIN.defaultValue): T {
    if (value) return value;
    this.tag(field, code);
    return fallback;
  }

  /** `value ?? fallback`, tagging `field` when the fallback was taken. */
  nullish<T>(field: string, value: T | null | undefined, fallback: T, code: OriginCode = ORIGIN.defaultValue): T {
    if (value !== null && value !== undefined) return value;
    this.tag(field, code);
    return fallback;
  }

  /** `Number(value) || 0`, tagging `field` when an empty became 0. */
  num(field: string, value: unknown, code: OriginCode = ORIGIN.defaultZero): number {
    const n = Number(value);
    if (n) return n;
    if (value === null || value === undefined || value === '' || Number.isNaN(n)) this.tag(field, code);
    return 0;
  }

  /**
   * Adds the tracker's live marks. `fieldMap` maps a form field path to the
   * audit field name(s); a path it does not list is not part of this save.
   */
  fromTracker(
    values: unknown,
    fieldMap: AuditFieldMap,
    rowKey: string = this.rowKey,
    tracker: OriginTracker = originTracker
  ): this {
    const normalizedMap: Record<string, string | readonly string[]> = {};
    for (const [formField, mapped] of Object.entries(fieldMap)) normalizedMap[normalizePath(formField)] = mapped;
    const live = tracker.live((field) => getPath(values, field), Object.keys(normalizedMap));
    for (const [formField, code] of live) {
      const mapped = normalizedMap[formField];
      for (const auditField of typeof mapped === 'string' ? [mapped] : mapped) {
        // A default the payload applied itself is more specific than a mark.
        if (!this.origins[rowKey]?.[auditField]) this.tag(auditField, code, rowKey);
      }
    }
    return this;
  }

  /**
   * fromTracker for a tab that is a list of rows (kemasan, containers,
   * documents): each item of the array at `arrayPath` is its own audit row.
   * `fieldMap` is relative to the item ("jumlahKemasan", not
   * "kemasan.0.jumlahKemasan"); `rowKey` names the item's audit row, or null
   * when the item has no stable identity to tag.
   */
  fromTrackerRows(
    values: unknown,
    arrayPath: string,
    // biome-ignore lint/suspicious/noExplicitAny: each list's row-key function types its own items
    rowKey: (item: any, index: number) => string | null,
    fieldMap: AuditFieldMap,
    tracker: OriginTracker = originTracker
  ): this {
    const items = getPath(values, arrayPath);
    if (!Array.isArray(items)) return this;
    items.forEach((item, index) => {
      const key = rowKey(item, index);
      if (!key) return;
      const itemMap: AuditFieldMap = {};
      for (const [field, mapped] of Object.entries(fieldMap)) {
        itemMap[`${normalizePath(arrayPath)}.${index}.${field}`] = mapped;
      }
      this.fromTracker(values, itemMap, key, tracker);
    });
    return this;
  }

  get(): FieldOrigins {
    return this.origins;
  }
}
